import React, { useState, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";

import firebase from "firebaseFactory";
import {
  FormSingleFieldRow,
  Form,
  FormInput,
  FormSubmit,
  FormSelect,
} from "components/Form";
import { Button } from "components/Button";
import { useFirebaseFunction } from "hooks";
import { sendPasswordResetEmail, getAppUrl, UserRole } from "modules/auth";

import { Page } from "../layout/Page";

interface FormData {
  displayName: string;
  email: string;
  siteIds: string;
  role: string;
}

interface UserData {
  displayName: string;
  email: string;
  siteIds: string[];
  role: string;
}

export const CreateEditUser = ({
  getUser = firebase.function("getUser"),
  createUser = firebase.function("createUser"),
  updateUser = firebase.function("updateUser"),
  resendUserInvite = firebase.function("resendUserInvite"),
}) => {
  const { userId } = useParams();
  const history = useHistory();

  const [serverError, setServerError] = useState<string>();
  const [status, setStatus] = useState<string>();
  const { register, handleSubmit, errors, formState, reset } = useForm<
    FormData
  >();
  const { data: user, isLoading } = useFirebaseFunction<UserData>(
    getUser,
    { userId },
    { shouldFetchData: userId !== undefined }
  );
  // set form defaults when user changes (on load)
  useMemo(() => {
    reset(
      user && {
        displayName: user.displayName,
        email: user.email,
        role: user.role,
        siteIds: user.siteIds.join(", "),
      }
    );
  }, [user, reset]);

  const onClickSubmit = handleSubmit(
    async ({ email, displayName, role, siteIds }) => {
      /* */
      const siteIdsArray = siteIds
        .split(",")
        .map((siteId) => siteId.trim())
        .filter((siteId) => siteId !== "");
      const {
        data: { error },
      } = userId
        ? await updateUser.action({
            userId,
            email,
            displayName,
            siteIds: siteIdsArray,
            role,
          })
        : await createUser.action({
            appUrl: getAppUrl(),
            email,
            displayName,
            siteIds: siteIdsArray,
            role,
          });
      if (error) {
        setServerError(error.message);
        return;
      }

      history.push(`/admin/users/`);
    }
  );
  const onSendLostPassword = async () => {
    await sendPasswordResetEmail(user!.email);
    setStatus("Lost password email sent");
  };
  const onSendInvite = async () => {
    await resendUserInvite.action({ email: user!.email, appUrl: getAppUrl() });
    setStatus("Invitation email sent");
  };

  return (
    <Page
      isLoading={isLoading}
      status={status}
      titleButtons={
        userId && (
          <>
            <Button
              isLoading={isLoading}
              className="mr-2"
              onClick={onSendLostPassword}
            >
              Send lost password
            </Button>
            <Button isLoading={isLoading} onClick={onSendInvite}>
              Send invitation
            </Button>
          </>
        )
      }
    >
      <Form onSubmit={onClickSubmit} serverError={serverError}>
        <FormSingleFieldRow>
          <FormInput
            name="displayName"
            label="Name"
            required
            register={register}
            errors={errors}
          />
        </FormSingleFieldRow>
        <FormSingleFieldRow>
          <FormInput
            name="email"
            label="Email address"
            required
            register={register}
            errors={errors}
          />
        </FormSingleFieldRow>
        <FormSingleFieldRow>
          <FormInput
            name="siteIds"
            label="Site Ids"
            note="Comma delimited"
            register={register}
            errors={errors}
          />
        </FormSingleFieldRow>
        <FormSingleFieldRow>
          <FormSelect
            name="role"
            label="Role"
            options={Object.values(UserRole)
              .filter(
                (x) => typeof x === "string" && x !== UserRole[UserRole.None]
              )
              .map((x) => ({ text: x.toString(), value: x.toString() }))}
            placeholder="(Select role)"
            register={register}
            errors={errors}
            required
          />
        </FormSingleFieldRow>
        <FormSubmit
          isLoading={isLoading || formState.isSubmitting}
          label={userId ? "Save user" : "Create user"}
        />
      </Form>
    </Page>
  );
};
